import React, { Component } from "react";
import PropTypes from "prop-types";
import { Animated, KeyboardAvoidingView, Modal, PanResponder, Platform, StyleSheet, TouchableOpacity, View } from "react-native";

const SUPPORTED_ORIENTATIONS = ["portrait", "portrait-upside-down", "landscape", "landscape-left", "landscape-right"];

class RBSheet extends Component {
    constructor(props) {
        super(props);
        this.state = {
            modalVisible: false,
            animatedHeight: new Animated.Value(0),
            pan: new Animated.ValueXY(),
            height: props.height,
            expanded: false
        };

        this.createPanResponder(props);
    }

    setModalVisible(visible, props) {
        const { minClosingHeight, openDuration, closeDuration, onClose, onOpen, height, expandOnDrag } = this.props;
        const { animatedHeight, pan } = this.state;
        if (visible) {
            Animated.timing(animatedHeight, {
                useNativeDriver: false,
                toValue: this.state.height,
                duration: openDuration,
            }).start(({ finished }) => {
                if (finished) {
                    this.setState({ modalVisible: visible, expanded: false }, () => {
                        if (typeof onOpen === "function") {
                            onOpen(props);
                        }
                    });
                }
            });
        } else {
            Animated.timing(animatedHeight, {
                useNativeDriver: false,
                toValue: minClosingHeight,
                duration: closeDuration,
            }).start(({ finished }) => {
                if (finished) {
                    pan.setValue({ x: 0, y: 0 });
                    this.setState({
                        modalVisible: visible,
                        expanded: false,
                        animatedHeight: expandOnDrag ? this.state.animatedHeight : new Animated.Value(0),
                        height: height,
                    }, () => {
                        if (typeof onClose === "function") {
                            onClose(props);
                        }
                    });
                }
            });
        }
    }

    createPanResponder(props) {
        const { closeOnDragDown, expandOnDrag } = props;
        const { pan, animatedHeight } = this.state;
        this.panResponder = PanResponder.create({
            onStartShouldSetPanResponder: () => closeOnDragDown,
            onPanResponderMove: (e, gestureState) => {
                if (gestureState.dy > 0) {
                    Animated.event([null, { dy: pan.y }], { useNativeDriver: false })(e, gestureState);
                } else if (expandOnDrag) {
                    this.setState(
                        {
                            height: this.props.maxHeight,
                            // height: this.state.height - gestureState.dy > this.state.maxHeight ? this.state.maxHeight : this.state.height - gestureState.dy,
                        },
                        () => {
                            if (this.props.height < this.state.height) {
                                // animatedHeight.setValue(this.props.height);
                                Animated.timing(animatedHeight, {
                                    useNativeDriver: false,
                                    toValue: this.state.height,
                                    duration: 0,
                                }).start();
                            }
                        }
                    );
                }
            },
            onPanResponderRelease: (e, gestureState) => {
                if (this.state.height / 4 - gestureState.dy < 0) {
                    this.setModalVisible(false);
                } else if (this.state.height > this.props.maxHeight) {
                    this.setState({
                        height: this.props.maxHeight,
                    });
                } else {
                    if (!this.state.expanded) {
                        Animated.spring(pan, { toValue: { x: 0, y: 0 }, useNativeDriver: false }).start();
                        this.setState({ expanded: this.state.height === this.props.maxHeight });
                    } else if (gestureState.dy === 0) {
                        //this.setModalVisible(false);
                        Animated.timing(this.state.animatedHeight, { useNativeDriver: false, toValue: this.props.height, duration: 0, }).start();
                        this.setState({ expanded: false, height: this.props.height });
                    } else {
                        Animated.spring(pan, { toValue: { x: 0, y: 0 }, useNativeDriver: false }).start();
                        this.setState({ expanded: gestureState.dy < 0 });
                    }
                }
            },
        });
    }

    open(props) {
        this.setModalVisible(true, props);
    }

    close(props) {
        this.setModalVisible(false, props);
    }

    isModalVisible() {
        return this.state.modalVisible;
    }

    render() {
        const { animationType, closeOnDragDown, dragFromTopOnly, closeOnPressMask, closeOnPressBack, children, customStyles, keyboardAvoidingViewEnabled, isLoading, renderLoadingComponent } = this.props;
        const { animatedHeight, pan, modalVisible } = this.state;
        const panStyle = {
            transform: pan.getTranslateTransform(),
        };

        return (
            <Modal
                transparent
                animationType={animationType}
                visible={modalVisible}
                supportedOrientations={SUPPORTED_ORIENTATIONS}
                onRequestClose={() => {
                    if (closeOnPressBack) {
                        this.setModalVisible(false);
                    }
                }}>
                <KeyboardAvoidingView enabled={keyboardAvoidingViewEnabled} behavior="padding" style={[styles.wrapper, customStyles.wrapper]}>
                    <TouchableOpacity style={styles.mask} activeOpacity={1} onPress={() => (closeOnPressMask ? this.close() : null)} />
                    <Animated.View {...!dragFromTopOnly && this.panResponder.panHandlers} style={[panStyle, styles.container, { height: animatedHeight }, customStyles.container]}>
                        {closeOnDragDown && (
                            <View {...dragFromTopOnly && this.panResponder.panHandlers} style={styles.draggableContainer}>
                                <View style={[styles.draggableIcon, customStyles.draggableIcon]} />
                            </View>
                        )}
                        {isLoading && renderLoadingComponent
                            ? renderLoadingComponent(this.state.height)
                            : children}
                    </Animated.View>
                </KeyboardAvoidingView>
            </Modal>
        );
    }
}

const styles = StyleSheet.create({
    wrapper: {
        flex: 1,
        backgroundColor: "#00000077",
    },
    mask: {
        flex: 1,
        backgroundColor: "transparent",
    },
    container: {
        backgroundColor: "#fff",
        width: "100%",
        height: 0,
        overflow: "hidden",
    },
    draggableContainer: {
        width: "100%",
        alignItems: "center",
        backgroundColor: "transparent",
        paddingTop: 5
    },
    draggableIcon: {
        width: 35,
        height: 5,
        borderRadius: 5,
        margin: 10,
        backgroundColor: "#ccc",
    },
});

RBSheet.propTypes = {
    animationType: PropTypes.oneOf(["none", "slide", "fade"]),
    height: PropTypes.number,
    maxHeight: PropTypes.number,
    minClosingHeight: PropTypes.number,
    openDuration: PropTypes.number,
    closeDuration: PropTypes.number,
    closeOnDragDown: PropTypes.bool,
    closeOnPressMask: PropTypes.bool,
    dragFromTopOnly: PropTypes.bool,
    expandOnDrag: PropTypes.bool,
    closeOnPressBack: PropTypes.bool,
    keyboardAvoidingViewEnabled: PropTypes.bool,
    customStyles: PropTypes.objectOf(PropTypes.object),
    onClose: PropTypes.func,
    onOpen: PropTypes.func,
    onExpandSheet: PropTypes.func,
    children: PropTypes.node,
    isLoading: PropTypes.bool,
    renderLoadingComponent: PropTypes.func,
};

RBSheet.defaultProps = {
    animationType: "none",
    height: 260,
    maxHeight: 630,
    minClosingHeight: 0,
    openDuration: 300,
    closeDuration: 200,
    closeOnDragDown: false,
    dragFromTopOnly: false,
    expandOnDrag: false,
    closeOnPressMask: true,
    closeOnPressBack: true,
    keyboardAvoidingViewEnabled: Platform.OS === "ios",
    customStyles: {},
    onClose: null,
    onOpen: null,
    children: <View />,
    isLoading: false,
    renderLoadingComponent: null,
};

export default RBSheet;
